import 'react-datepicker/dist/react-datepicker.css';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Search } from 'lucide-react';
import moment from 'moment';
import { API } from '../../Common/Api';
import CommonTable from '../../Common/CommonTable';
import { ErrorToast } from '../../Utils/toast';
import TextInput from '../../Commponent/TextInput';

// Admin-only: debit transactions from the external portal.
const Transactions = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Filters
  const [fastagId, setFastagId] = useState('');
  const [debouncedFastag, setDebouncedFastag] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFastag(fastagId), 700);
    return () => clearTimeout(t);
  }, [fastagId]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await API.GetDebitTransactions({
        pageNumber: currentPage,
        pageSize: itemsPerPage,
        startDate: fromDate ? moment(fromDate).format('YYYY-MM-DD') : '',
        endDate: toDate ? moment(toDate).format('YYYY-MM-DD') : '',
        fastagId: debouncedFastag,
      });
      if (res?.status === 200 && res?.success) {
        const data = Array.isArray(res.data) ? res.data : [];
        setRows(data);
        const t = res.pagination?.total || 0;
        setTotal(t);
        setTotalPages(res.pagination?.totalPages || Math.max(1, Math.ceil(t / itemsPerPage)));
      } else {
        setRows([]);
        setTotal(0);
        setTotalPages(1);
        if (res?.message) ErrorToast(res.message);
      }
    } catch (err) {
      setRows([]);
      setTotal(0);
      setTotalPages(1);
      ErrorToast('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, debouncedFastag, fromDate, toDate]);

  const StatusBadge = ({ status }) => {
    const base = 'px-2 py-[2px] rounded-full text-xs font-medium';
    if (status === 'COMPLETED') return <span className={`${base} bg-green-100 text-green-900`}>Completed</span>;
    if (status === 'PENDING') return <span className={`${base} bg-yellow-100 text-yellow-900`}>Pending</span>;
    if (status === 'FAILED') return <span className={`${base} bg-red-100 text-red-900`}>Failed</span>;
    return <span className={`${base} bg-gray-100 text-gray-700`}>{status || 'N/A'}</span>;
  };

  const columns = [
    { label: 'Tag Ref ID', render: (r) => r.custRefId || r.tagId || r.fastagId || 'N/A' },
    { label: 'Vehicle Number', render: (r) => r.vehicleNumber || 'N/A' },
    { label: 'Vehicle Class', render: (r) => r.vehicleClass || 'N/A' },
    {
      label: 'Amount',
      tdClassName: 'justify-end',
      render: (r) => (r.amount != null ? `₹ ${Number(r.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : 'N/A'),
    },
    { label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    {
      label: 'Transaction Date',
      render: (r) => (r.transactionDate && moment(r.transactionDate).isValid() ? moment(r.transactionDate).local().format('DD MMMM, YYYY, hh:mm A') : 'N/A'),
    },
  ];

  const clearFilters = () => {
    setFastagId('');
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-4 mt-6">
        <span className="w-1.5 h-7 rounded-full bg-[#EE3B31]"></span>
        <h2 className="text-2xl font-bold text-gray-900 leading-tight">Transactions</h2>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="relative md:col-span-12 lg:col-span-4">
            <TextInput
              name="fastagId"
              placeholder="Tag Ref / Vehicle No / Chassis No"
              value={fastagId}
              onChange={(e) => { setFastagId(e.target.value); setCurrentPage(1); }}
              inputClass="pl-5 pr-10 py-3 text-sm"
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search className="w-4 h-4" />
            </div>
          </div>

          <div className="relative w-full md:col-span-6 lg:col-span-3">
            <DatePicker
              selected={fromDate}
              onChange={(d) => { setFromDate(d); setCurrentPage(1); }}
              dateFormat="dd MMM, yyyy"
              placeholderText="From Date"
              isClearable
              maxDate={toDate || new Date()}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#EE3B31] focus:ring-2 focus:ring-[#EE3B31]/20"
            />
          </div>

          <div className="relative w-full md:col-span-6 lg:col-span-3">
            <DatePicker
              selected={toDate}
              onChange={(d) => { setToDate(d); setCurrentPage(1); }}
              dateFormat="dd MMM, yyyy"
              placeholderText="To Date"
              isClearable
              minDate={fromDate || undefined}
              maxDate={new Date()}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#EE3B31] focus:ring-2 focus:ring-[#EE3B31]/20"
            />
          </div>

          <div className="flex justify-start lg:justify-end md:col-span-12 lg:col-span-2">
            <button
              onClick={clearFilters}
              className="border border-[#EE3B31] text-[#EE3B31] py-2.5 px-5 rounded-lg font-semibold transition-colors hover:bg-[#EE3B31] hover:text-white text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="p-0 space-y-8">
        <CommonTable
          columns={columns}
          data={rows}
          emptyMessage={loading ? 'Loading...' : 'No Transactions Found'}
          isPagination={true}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          setCurrentPage={setCurrentPage}
          setItemsPerPage={setItemsPerPage}
          total={total}
          totalPages={totalPages}
          loading={loading}
          hideDateAndDownload={true}
        />
      </div>
    </>
  );
};

export default Transactions;
